import { MarketingChannel } from "@redotech/redo-billing-common/marketing-channel";
import * as zExt from "@redotech/rpc/ext";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";
import { parseElementId } from "../../email-builder/clickable-elements";
import { PhoneNumberProvider } from "../../phone/phone-number";
import { bandwidthWebhookSchema } from "../../text-messaging/bandwidth-zod";
import { smsMessageMetadataSchema_ } from "../../text-messaging/message-metadata";
import { RedoSmsErrorCategory } from "../../text-messaging/sms-error-codes";
import { telnyxMessageWebhookSchema } from "../../text-messaging/telnyx-zod";
import {
  attributableDiscountCodesSchema,
  baseCustomerEventSchema,
  CustomerEventSource,
  CustomerEventType,
} from "./base";
import {
  SesBounceEventSchema,
  SesClickEventSchema,
  SesComplaintEventSchema,
  SesDeliveryEventSchema,
  SesOpenEventSchema,
  SesSendEventSchema,
} from "./ses-events";

export enum BounceType {
  PERMANENT = "PERMANENT",
  TRANSIENT = "TRANSIENT",
  UNDETERMINED = "UNDETERMINED",
}

export enum ReputationImpact {
  LOW = "low",
  MEDIUM = "medium",
  HIGH = "high",
  SEVERE = "severe",
}

export enum OutreachType {
  SMS = "sms",
  MMS = "mms",
  EMAIL = "email",
}

export enum SmsDirection {
  INBOUND = "inbound",
  OUTBOUND = "outbound",
}

const smsWebhookCustomerEventTypes = [
  CustomerEventType.INBOUND_RECEIVED,
  CustomerEventType.OUTREACH_SEND,
  CustomerEventType.OUTREACH_DELIVERY,
  CustomerEventType.OUTREACH_FAILED,
  CustomerEventType.OUTREACH_OPEN,
] as const;
export type SmsWebhookCustomerEventType =
  (typeof smsWebhookCustomerEventTypes)[number];

// Outbound webhooks have full metadata with campaign/template context
const normalizedOutboundSmsWebhookSchema = z.object({
  provider: z.enum(PhoneNumberProvider),
  eventType: z.enum(smsWebhookCustomerEventTypes),
  messageId: z.string(),
  timestamp: z.coerce.date(),
  to: z.array(z.string()),
  from: z.string(),
  text: z.string().optional(),
  media: z
    .array(
      z.object({
        url: z.string(),
        contentType: z.string().optional(), // provided by Telnyx, but not Bandwidth
      }),
    )
    .optional(),
  segmentCount: z.number(),
  messageType: z.literal(OutreachType.SMS).or(z.literal(OutreachType.MMS)),
  direction: z.literal(SmsDirection.OUTBOUND),
  metadata: smsMessageMetadataSchema_,
  // Carrier fee in micros (only present for Telnyx, undefined for Bandwidth)
  carrierFeeMicros: z.number().optional(),
  // Error information (only present for MESSAGE_FAILED events)
  errors: z
    .array(
      z.object({
        code: z.string(),
        message: z.string(),
        shouldCreateSendEvent: z.boolean(),
        redoErrorCategory: createEnum(RedoSmsErrorCategory, {
          enumName: "RedoSmsErrorCategory",
        }).nullish(),
      }),
    )
    .optional(),
  // Provider's detection of SMS action (STOP, START, HELP, etc.) - for inbound messages
  detectedSmsAction: z.string().nullish(),
  // The original provider-specific webhook (Bandwidth or Telnyx)
  // This is stored both here and at the root CustomerEvent.rawEvent for compatibility
  rawEvent: z
    .union([telnyxMessageWebhookSchema, bandwidthWebhookSchema])
    .optional(),
});
export type NormalizedOutboundSmsWebhook = z.infer<
  typeof normalizedOutboundSmsWebhookSchema
>;

// Inbound webhooks have minimal metadata (just teamId and phoneNumber)
const normalizedInboundSmsWebhookSchema = normalizedOutboundSmsWebhookSchema
  .omit({ metadata: true })
  .extend({
    metadata: smsMessageMetadataSchema_.pick({ category: true }),
    direction: z.literal(SmsDirection.INBOUND),
  });

const normalizedSmsWebhookSchema_ = z.discriminatedUnion("direction", [
  normalizedOutboundSmsWebhookSchema,
  normalizedInboundSmsWebhookSchema,
]);
export const normalizedSmsWebhookSchema: z.ZodType<NormalizedSmsWebhook> =
  normalizedSmsWebhookSchema_;
export type NormalizedSmsWebhook = z.infer<typeof normalizedSmsWebhookSchema_>;

const normalizedSmsWebhookSchemaWithoutRawEvent_ = z.discriminatedUnion(
  "direction",
  [
    normalizedOutboundSmsWebhookSchema.omit({ rawEvent: true }),
    normalizedInboundSmsWebhookSchema.omit({ rawEvent: true }),
  ],
);
export const normalizedSmsWebhookSchemaWithoutRawEvent: z.ZodType<NormalizedSmsWebhookWithoutRawEvent> =
  normalizedSmsWebhookSchemaWithoutRawEvent_;

// Used for backwards compatibility at the DB layer, which stores the raw event at the root level
export type NormalizedSmsWebhookWithoutRawEvent = z.infer<
  typeof normalizedSmsWebhookSchemaWithoutRawEvent_
>;

const _OutreachAnalyticsEventBaseSchema = baseCustomerEventSchema.extend({
  template: z.string().optional(),
  automation: z.string().optional(),
  automationStepId: z.string().optional(),
  campaign: z.string().optional(),
  marketingMessageId: z.string().optional(),
  marketingConversationId: z.string().optional(),
  category: z.string().optional(),
  /**
   * The email address or phone number that the outreach was sent from.
   * Currently only populated for SES-based email events, not AMP emails or SMS,
   * therefore it should not be considered reliable.
   */
  fromChannelId: z.string().optional(),
  recipientId: z.string().optional(),
  channelId: z.string(),
  orderId: zExt.objectId().optional(),
  returnId: zExt.objectId().optional(),
  externalMessageId: z.string().optional(),
  templateName: z.string().optional(),
  discountCodes: attributableDiscountCodesSchema,
});

const _EmailOutreachEventBaseSchema = _OutreachAnalyticsEventBaseSchema.extend({
  channel: z.literal(MarketingChannel.EMAIL),
  recipientDomain: z.string().optional(),
  outgoingIp: z.string().optional(),
  configurationSet: z.string().optional(),
  ipPool: z.string().optional(),
  subject: z.string().optional(),
});
export type EmailOutreachBaseEvent = z.infer<
  typeof _EmailOutreachEventBaseSchema
>;

const rawSmsWebhookSchema_ = z.union([
  telnyxMessageWebhookSchema,
  bandwidthWebhookSchema,
]);
type RawSmsWebhook = z.infer<typeof rawSmsWebhookSchema_>;
const rawSmsWebhookSchema: z.ZodType<RawSmsWebhook> = rawSmsWebhookSchema_;

const _SmsOutreachEventBaseSchema = _OutreachAnalyticsEventBaseSchema.extend({
  channel: z.literal(MarketingChannel.SMS),
  rawEvent: rawSmsWebhookSchema.optional(),
  normalizedWebhook: normalizedSmsWebhookSchemaWithoutRawEvent.optional(),
});
export type SmsOutreachBaseEvent = z.infer<typeof _SmsOutreachEventBaseSchema>;

const messageCostSchema_ = z.object({
  totalMicros: z.number(),
  rate: z.object({ amountMicros: z.number(), currency: z.string() }).nullish(),
  currency: z.string().nullish(),
  count: z.number(),
  additionalFees: z
    .object({
      carrierFee: z.object({ amountMicros: z.number(), currency: z.string() }),
    })
    .nullish(),
  pricingContext: z.object({
    messageType: createEnum(OutreachType, { enumName: "OutreachType" }),
    isoCountryCode: z.string().nullish(),
  }),
});
export const messageCostSchema: z.ZodType<MessageCost> = messageCostSchema_;
export interface MessageCost extends z.infer<typeof messageCostSchema_> {}

const EmailOutreachSendEventSchema = _EmailOutreachEventBaseSchema.extend({
  eventType: z.literal(CustomerEventType.OUTREACH_SEND),
  rawEvent: SesSendEventSchema.optional(),
  cost: messageCostSchema.optional(),
});
export interface EmailOutreachSendEvent
  extends z.infer<typeof EmailOutreachSendEventSchema> {}

const SmsOutreachSendEventSchema = _SmsOutreachEventBaseSchema.extend({
  eventType: z.literal(CustomerEventType.OUTREACH_SEND),
  cost: messageCostSchema.optional(),
});
export interface SmsOutreachSendEvent
  extends z.infer<typeof SmsOutreachSendEventSchema> {}

export const OutreachSendEventSchema_ = z.discriminatedUnion("channel", [
  EmailOutreachSendEventSchema,
  SmsOutreachSendEventSchema,
]);
export const OutreachSendEventSchema: z.ZodType<OutreachSendEvent> =
  OutreachSendEventSchema_;
export type OutreachSendEvent = z.infer<typeof OutreachSendEventSchema_>;

const EmailOutreachDeliveryEventSchema = _EmailOutreachEventBaseSchema.extend({
  eventType: z.literal(CustomerEventType.OUTREACH_DELIVERY),
  rawEvent: SesDeliveryEventSchema.optional(),
  deliveryTimeMs: z.number().optional(),
  smtpResponse: z.string().optional(),
  remoteMtaIp: z.string().optional(),
});
export interface EmailOutreachDeliveryEvent
  extends z.infer<typeof EmailOutreachDeliveryEventSchema> {}

const SmsOutreachDeliveryEventSchema = _SmsOutreachEventBaseSchema.extend({
  eventType: z.literal(CustomerEventType.OUTREACH_DELIVERY),
});
export interface SmsOutreachDeliveryEvent
  extends z.infer<typeof SmsOutreachDeliveryEventSchema> {}

export const OutreachDeliveryEventSchema_ = z.discriminatedUnion("channel", [
  EmailOutreachDeliveryEventSchema,
  SmsOutreachDeliveryEventSchema,
]);
export const OutreachDeliveryEventSchema: z.ZodType<OutreachDeliveryEvent> =
  OutreachDeliveryEventSchema_;
export type OutreachDeliveryEvent = z.infer<
  typeof OutreachDeliveryEventSchema_
>;

// SMS doesn't have open events, only email does
const EmailOutreachOpenEventSchema = _EmailOutreachEventBaseSchema.extend({
  eventType: z.literal(CustomerEventType.OUTREACH_OPEN),
  rawEvent: SesOpenEventSchema.optional(),
  // Email open specific fields
  userAgent: z.string().optional(),
  ipAddress: z.string().optional(),
  source: createEnum(
    [CustomerEventSource.SES, CustomerEventSource.LINK_SHORTENER],
    {
      enumName: "CustomerEventSource",
      isPartial: true,
      originalEnum: CustomerEventSource,
    },
  ).optional(),
});
export interface EmailOutreachOpenEvent
  extends z.infer<typeof EmailOutreachOpenEventSchema> {}

export const OutreachOpenEventSchema_ = EmailOutreachOpenEventSchema;
export const OutreachOpenEventSchema: z.ZodType<OutreachOpenEvent> =
  OutreachOpenEventSchema_;
export type OutreachOpenEvent = EmailOutreachOpenEvent;

const EmailOutreachClickEventSchema = _EmailOutreachEventBaseSchema.extend({
  eventType: z.literal(CustomerEventType.OUTREACH_CLICK),
  rawEvent: SesClickEventSchema.optional(),
  // Email click specific fields
  userAgent: z.string().optional(),
  ipAddress: z.string().optional(),
  clickedUrl: z.string().optional(),
  /** An identifier for the clickable element that was clicked. Might have a semantic name, might not */
  elementId: z
    .string()
    .transform((val) => parseElementId(val))
    .optional(),
  /** Which email block the click occurred within */
  blockId: z.string().optional(),
  source: createEnum(
    [CustomerEventSource.SES, CustomerEventSource.LINK_SHORTENER],
    {
      enumName: "CustomerEventSource",
      isPartial: true,
      originalEnum: CustomerEventSource,
    },
  ).optional(),
});
export interface EmailOutreachClickEvent
  extends z.infer<typeof EmailOutreachClickEventSchema> {}

const SmsOutreachClickEventSchema = _SmsOutreachEventBaseSchema
  .extend({
    eventType: z.literal(CustomerEventType.OUTREACH_CLICK),
    // SMS click events (for links in SMS messages)
    userAgent: z.string().optional(),
    ipAddress: z.string().optional(),
    clickedUrl: z.string().optional(),
    source: z.literal(CustomerEventSource.LINK_SHORTENER).optional(),
  })
  .omit({ rawEvent: true, normalizedWebhook: true }); // No rawEvent for SMS clicks

export const OutreachClickEventSchema_ = z.discriminatedUnion("channel", [
  EmailOutreachClickEventSchema,
  SmsOutreachClickEventSchema,
]);
export const OutreachClickEventSchema: z.ZodType<OutreachClickEvent> =
  OutreachClickEventSchema_;

export type OutreachClickEvent = z.infer<typeof OutreachClickEventSchema_>;

// SMS doesn't have bounce events, only email does
const EmailOutreachBounceEventSchema = _EmailOutreachEventBaseSchema.extend({
  eventType: z.literal(CustomerEventType.OUTREACH_BOUNCE),
  bounceType: createEnum(BounceType, { enumName: "BounceType" }).optional(),
  rawEvent: SesBounceEventSchema.optional(),
  bounceSubtype: z.string().optional(),
  bounceDiagnosticCode: z.string().optional(),
  bounceStatus: z.string().optional(),
  reputationImpact: createEnum(ReputationImpact, {
    enumName: "ReputationImpact",
  }).optional(),
});
export interface EmailOutreachBounceEvent
  extends z.infer<typeof EmailOutreachBounceEventSchema> {}

export const OutreachBounceEventSchema_ = EmailOutreachBounceEventSchema;
export const OutreachBounceEventSchema: z.ZodType<OutreachBounceEvent> =
  OutreachBounceEventSchema_;
export type OutreachBounceEvent = EmailOutreachBounceEvent;

// SMS doesn't have complaint events, only email does
const EmailOutreachComplaintEventSchema = _EmailOutreachEventBaseSchema.extend({
  eventType: z.literal(CustomerEventType.OUTREACH_COMPLAINT),
  rawEvent: SesComplaintEventSchema.optional(),
  complaintFeedbackType: z.string().optional(),
  reputationImpact: createEnum(ReputationImpact, {
    enumName: "ReputationImpact",
  }).optional(),
});
export interface EmailOutreachComplaintEvent
  extends z.infer<typeof EmailOutreachComplaintEventSchema> {}

export const OutreachComplaintEventSchema_ = EmailOutreachComplaintEventSchema;
export const OutreachComplaintEventSchema: z.ZodType<OutreachComplaintEvent> =
  OutreachComplaintEventSchema_;
export type OutreachComplaintEvent = EmailOutreachComplaintEvent;

// Failed events are only for SMS, email failures come through as bounces
const SmsOutreachFailedEventSchema = _SmsOutreachEventBaseSchema.extend({
  eventType: z.literal(CustomerEventType.OUTREACH_FAILED),
  errorCode: z.string(),
  redoErrorCategory: createEnum(RedoSmsErrorCategory, {
    enumName: "RedoSmsErrorCategory",
  }).nullish(),
  errorMessage: z.string().optional(),
});
export interface SmsOutreachFailedEvent
  extends z.infer<typeof SmsOutreachFailedEventSchema> {}

export const OutreachFailedEventSchema_ = SmsOutreachFailedEventSchema;
export const OutreachFailedEventSchema: z.ZodType<OutreachFailedEvent> =
  OutreachFailedEventSchema_;
export type OutreachFailedEvent = SmsOutreachFailedEvent;

export const InboundReceivedEventSchema_ = baseCustomerEventSchema.extend({
  eventType: z.literal(CustomerEventType.INBOUND_RECEIVED),
  category: z.string().optional(),
  channel: createEnum(MarketingChannel, { enumName: "MarketingChannel" }),
  channelId: z.string(),
  marketingConversationId: z.string().optional(),
  cost: messageCostSchema.optional(),
  rawEvent: rawSmsWebhookSchema.optional(),
  normalizedWebhook: normalizedSmsWebhookSchemaWithoutRawEvent.optional(),
});
export const InboundReceivedEventSchema: z.ZodType<InboundReceivedEvent> =
  InboundReceivedEventSchema_;
export interface InboundReceivedEvent
  extends z.infer<typeof InboundReceivedEventSchema_> {}

export type CampaignRecipientMetricEvent =
  | OutreachOpenEvent
  | OutreachClickEvent;
